/**
 * Typed API Client — Fetch wrapper with JWT, error handling
 */

const API_BASE = "/api";
const AUTH_BASE = "/auth";

// ─── Auth helpers ─────────────────────────────────────────────────────────────
function getToken(): string | null {
  return localStorage.getItem("jwt");
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  base = API_BASE,
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) ?? {}),
  };

  if (headers["Content-Type"] === "none") {
    delete headers["Content-Type"];
  } else if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${base}${path}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem("jwt");
    window.location.href = "/index.html";
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    let msg = res.statusText;
    try {
      const text = await res.text();
      try {
        const j = JSON.parse(text);
        if (typeof j.error === "string") {
          msg = j.error;
        } else if (j.errors && typeof j.errors === "object") {
          // ASP.NET Core ProblemDetails ValidationError
          const firstKey = Object.keys(j.errors)[0];
          msg = Array.isArray(j.errors[firstKey])
            ? j.errors[firstKey][0]
            : j.errors[firstKey];
        } else {
          msg = j.message || j.title || msg;
        }
      } catch {
        msg = text || msg;
      }
    } catch {}
    throw new Error(msg);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const get = <T>(path: string, base?: string) =>
  apiFetch<T>(path, { method: "GET" }, base);
const post = <T>(path: string, body: unknown, base?: string) =>
  apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) }, base);
const put = <T>(path: string, body: unknown, base?: string) =>
  apiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) }, base);
const del = <T>(path: string, base?: string) =>
  apiFetch<T>(path, { method: "DELETE" }, base);
const patch = <T>(path: string, body: unknown, base?: string) =>
  apiFetch<T>(path, { method: "PATCH", body: JSON.stringify(body) }, base);

function buildQuery(params: Record<string, string> = {}): string {
  const q = new URLSearchParams(params).toString();
  return q ? `?${q}` : "";
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    post<{
      token: string;
      role: string;
      name: string;
      tenantId?: string;
      email: string;
    }>("/login", { email, password }, AUTH_BASE),
};

// ─── Admin — Dashboard ────────────────────────────────────────────────────────
export const dashboardApi = {
  get: () => get<any>("/admin/dashboard"),
  getGlobal: () => get<any>("/admin/stats/global"),
};

// ─── Admin — Tenants ─────────────────────────────────────────────────────────
export const tenantsApi = {
  list: () => get<any>("/admin/tenants"),
  get: (id: string) => get<any>(`/admin/tenants/${id}`),
  create: (data: any) => post<any>("/admin/tenants", data),
  update: (id: string, data: any) => put<any>(`/admin/tenants/${id}`, data),
  suspend: (id: string) => patch<any>(`/admin/tenants/${id}/suspend`, {}),
  activate: (id: string) => patch<any>(`/admin/tenants/${id}/activate`, {}),
  delete: (id: string) => del<void>(`/admin/tenants/${id}`),
  togglePause: (id: string, pause: boolean) =>
    post<any>(`/admin/tenants/${id}/toggle-pause?pause=${pause}`, {}),
};

// ─── Admin — Plans ────────────────────────────────────────────────────────────
export const plansApi = {
  list: () => get<any>("/admin/plans"),
  get: (id: string) => get<any>(`/admin/plans/${id}`),
  create: (data: any) => post<any>("/admin/plans", data),
  update: (id: string, data: any) => put<any>(`/admin/plans/${id}`, data),
  delete: (id: string) => del<void>(`/admin/plans/${id}`),
};

// ─── Admin — Sessions (global) ───────────────────────────────────────────────
export const adminSessionsApi = {
  list: () => get<any[]>("/admin/sessions"),
  getQr: (id: string) =>
    get<{ qr?: string; status?: string }>(`/admin/sessions/${id}/qr`),
  getAudit: (sessionId?: string, page = 1) =>
    get<any>(`/admin/sessions/audit?sessionId=${sessionId ?? ""}&page=${page}`),
};

// ─── Admin — Messages (global) ───────────────────────────────────────────────
export const adminMessagesApi = {
  list: (params: Record<string, string> = {}) =>
    get<any>(`/admin/messages${buildQuery(params)}`),
};

// ─── Admin — API Keys (global) ───────────────────────────────────────────────
export const adminApiKeysApi = {
  list: () => get<any[]>("/admin/api-keys"),
  revoke: (id: string) => patch<void>(`/admin/api-keys/${id}/revoke`, {}),
};

// ─── Admin — Queue stats ──────────────────────────────────────────────────────
export const statsApi = {
  queue: () => get<any>("/admin/redis/queues"),
  peek: (key: string, limit: number = 50) =>
    get<any[]>(
      `/admin/redis/queues/peek?key=${encodeURIComponent(key)}&limit=${limit}`,
    ),
  purge: (key: string) =>
    del<any>(`/admin/redis/queues/purge?key=${encodeURIComponent(key)}`),
  validations: () => get<any>("/admin/redis/validations"),
  listValidations: (limit: number = 200) =>
    get<any[]>(`/admin/redis/validations/list?limit=${limit}`),
  clearValidations: () => del<any>("/admin/redis/validations/clear"),
};

// ─── Admin — Settings ────────────────────────────────────────────────────────
export const settingsApi = {
  get: () => get<any>("/admin/settings"),
  update: (data: any) => put<any>("/admin/settings", data),
};

// ─── Admin — Account ─────────────────────────────────────────────────────────
export const accountApi = {
  me: () => get<any>("/account/me"),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    post<void>("/account/change-password", data),
};

// ─── Client — Sessions ───────────────────────────────────────────────────────
export const sessionsApi = {
  list: () => get<any[]>("/sessions"),
  get: (id: string) => get<any>(`/sessions/${id}`),
  create: (name: string) => post<any>("/sessions", { name }),
  delete: (id: string) => del<void>(`/sessions/${id}`),
  getQr: (id: string) =>
    get<{ qr?: string; status?: string }>(`/sessions/${id}/qr`),
};

// ─── Client — Contacts (CRM Lite) ─────────────────────────────────────────────
export const contactsApi = {
  list: (params: Record<string, string> = {}) =>
    get<any>(`/contacts${buildQuery(params)}`),
  get: (id: string) => get<any>(`/contacts/${id}`),
  create: (data: any) => post<any>("/contacts", data),
  update: (id: string, data: any) => put<any>(`/contacts/${id}`, data),
  delete: (id: string) => del<void>(`/contacts/${id}`),
  getDistinctTags: () => get<string[]>("/contacts/tags"),
  upload: (formData: FormData) =>
    apiFetch<any>("/contacts/upload", {
      method: "POST",
      body: formData,
      headers: { "Content-Type": "none" }, // fetch lo autocompleta con boundary
    }),
};

// ─── Client — Campaigns ───────────────────────────────────────────────────────
export const campaignsApi = {
  list: (params: Record<string, string> = {}) =>
    get<any>(`/campaigns${buildQuery(params)}`),
  get: (id: string) => get<any>(`/campaigns/${id}`),
  create: (data: any) => post<any>("/campaigns", data),
  delete: (id: string) => del<void>(`/campaigns/${id}`),
  uploadContacts: (campaignId: string, formData: FormData) =>
    apiFetch<any>(`/campaigns/${campaignId}/contacts/upload`, {
      method: "POST",
      body: formData,
      headers: { "Content-Type": "none" },
    }),
  update: (id: string, data: any) => put<any>(`/campaigns/${id}`, data),
  getContacts: (id: string) => get<any[]>(`/campaigns/${id}/contacts`),
  exportReport: (id: string) => get<any>(`/campaigns/${id}/export`),
  downloadCsvTemplate: () => get<any>("/campaigns/templates/csv"),
  downloadExcelTemplate: () => get<any>("/campaigns/templates/excel"),
};

// ─── Client — Messages ───────────────────────────────────────────────────────
export const messagesApi = {
  list: (params: Record<string, string> = {}) =>
    get<any>(`/messages${buildQuery(params)}`),
  sendText: (data: {
    sessionId: string;
    toNumber: string;
    message: string;
    idempotencyKey?: string;
  }) => post<any>("/messages/send-text", data),
  sendPdf: (data: {
    sessionId: string;
    toNumber: string;
    file: string;
    filename: string;
    message?: string;
    idempotencyKey?: string;
  }) => post<any>("/messages/send-pdf", data),
  sendMedia: (data: {
    sessionId: string;
    toNumber: string;
    media: string;
    link: string;
    caption?: string;
    idempotencyKey?: string;
  }) => post<any>("/messages/send-media", data),
  getStatus: (id: string) => get<any>(`/messages/${id}/status`),
};

// ─── Client — API Keys ───────────────────────────────────────────────────────
export const apiKeysApi = {
  list: () => get<any[]>("/api-keys"),
  create: (data: { name: string; rateLimitPerMinute?: number }) =>
    post<any>("/api-keys", data),
  revoke: (id: string) => del<void>(`/api-keys/${id}`),
};

// ─── Client — Webhooks ───────────────────────────────────────────────────────
export const webhooksApi = {
  get: () => get<any>("/tenant/webhook"),
  update: (data: {
    callbackUrl: string;
    callbackSecret?: string;
    notifyEvents?: string[];
  }) => put<void>("/tenant/webhook", data),
  test: () => post<void>("/tenant/webhook/test", {}),
};

// ─── Client — Dashboard ──────────────────────────────────────────────────────
export const clientDashboardApi = {
  get: () =>
    get<{
      messagesThisMonth: number;
      quotaUsed: number;
      quotaLimit: number;
      activeSessions: number;
      failedToday: number;
      messagesByDay: { date: string; success: number; failure: number }[];
      recentMessages: any[];
      planName: string;
      maxSessions: number;
      rateLimitPerMinute: number;
      planExpiry?: string;
    }>("/portal/dashboard"),
};

// ─── Client — Billing ────────────────────────────────────────────────────────
export const billingApi = {
  getPlans: () => get<any[]>("/billing/plans"),
};
